import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { PermissionsService } from '@app/modules/auth';
import { map } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { PendoConfig } from './pendo.model';
import { DEFAULT_SETTINGS } from './settings';

declare var pendo: any;

@Injectable({
  providedIn: 'root'
})
export class PendoService {
  private renderer: Renderer2;

  constructor(
    private permissionsService: PermissionsService,
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document,
    @Inject('applicationId') private readonly applicationId: string,
    @Inject('pendoParams') private readonly pendoParams: any
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  missingValueText = 'unknown';

  getConfig(): Observable<any> {
    return combineLatest([
      this.permissionsService.getUser(),
      this.permissionsService.getUserProfiles(),
      this.permissionsService.getCurrentCompanyContext$()
    ]).pipe(
      map(([user, profiles, companyContext]) => {
        const profile = profiles.filter(profile => {
          return profile.applicationId === this.applicationId && companyContext.id
            ? profile.companyId === companyContext.id
            : true;
        });
        return { user, profile, companyContext };
      }),
      map((res: any) => {
        const company = res.companyContext;
        const coreUser = res.user;
        const coreUserProfile = res.profile;

        return {
          visitor: {
            UserUuid: coreUser ? coreUser.id : this.missingValueText,
            id: coreUser?.identityProviderId,
            firstName: coreUser?.firstName,
            lastName: coreUser?.lastName,
            email: coreUser?.email,
            created: coreUser?.created,
            modified: coreUser?.modified,
            employeeNumber: coreUserProfile?.employeeNumber,
            companyEmail: coreUserProfile?.companyEmail,
            roleName: coreUserProfile?.roles ? coreUserProfile?.roles[0]?.name : '',
            roleDescription: coreUserProfile?.roles ? coreUserProfile?.roles[0]?.description : '',
            roleIsRestricted: coreUserProfile?.roles ? coreUserProfile?.roles[0]?.restricted : '',
            isZonarUser: this._isZonarUserProfile(coreUserProfile),
            // homeLocationName: homeLocation?.name,
            // homeLocationCity: homeLocation?.address?.city,
            // homeLocationState: homeLocation?.address?.state,
            // homeLocationZip: homeLocation?.address?.zip,
            // homeLocationCounty: homeLocation?.address?.county,
            // homeLocationCountry: homeLocation?.address?.country,
            // homeLocationTimezone: homeLocation?.timezone,
            isIosWebView: this.isIosWebView(),
            isAndroidWebView: this.isAndroidWebView()
          },
          account: {
            CompanyUuid: company ? company.id : this.missingValueText,
            CompanyName: company ? company.name : this.missingValueText,
            id: company?.id,
            name: company?.name,
            anonymizedData: company?.anonymizedData,
            created: company?.created,
            modified: company?.modified
          }
        };
      })
    );
  }

  _isZonarUserProfile(userProfile) {
    // the V1 entity APIs use a null companyId and empty division list to specify global company access for zonar users
    return userProfile?.companyId === null && userProfile?.divisions?.length === 0;
  }

  loadScript(): HTMLScriptElement {
    /* If there is no api key for webview or browser, then the default api keys will be used from settings.ts */
    const pendoWebApiKey = this.pendoParams?.key || DEFAULT_SETTINGS.pendo.key;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = `${this.pendoParams?.url || DEFAULT_SETTINGS.pendo.url}${pendoWebApiKey}/pendo.js`;
    /* In order to track separate events for browser and webview,
     * we initialize the Pendo class with webview specific api key and url
     * In order to initialize the Pendo class with webview specific api key and url,
     * pendo config should be provided in the following format
     * The default URL and api keys will be used if the pendo config is not provided
     *  pendo: {
     *   url: `https://cdn.eu.pendo.io/agent/static/`,
     *   key: '47e63a49-4477-47c2-4ea6-0f032e1785d5',
     *   gtcMobile: {
     *    url: '<pendo url for webview>',
     *    key: '<pendo api key for webview>'
     *   }

    */
    script.async = true;
    script.defer = true;
    this.renderer.appendChild(document.head, script);
    return script;
  }

  // detect if the user agent is an ios webview
  isIosWebView() {
    return /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(navigator.userAgent);
  }

  // detect if the user agent is an android webview
  isAndroidWebView() {
    return /Android/.test(navigator.userAgent) && /AppleWebKit/.test(navigator.userAgent);
  }

  // returns true if the client is a web view in a mobile application
  isMobileWebView() {
    return this.isIosWebView() || this.isAndroidWebView();
  }

  initialize(additionalVisitorMetadata?: object, additionalAccountMetadata?: object) {
    this.loadScript().onload = () => {
      this.getConfig().subscribe(pendoConfig => {
        const fullPendoConfig: PendoConfig = {
          visitor: { ...additionalVisitorMetadata, ...pendoConfig.visitor },
          account: { ...additionalAccountMetadata, ...pendoConfig.account }
        };
        pendo.initialize(fullPendoConfig);

        /* Ping analytics event on every 60 seconds */
        const userTrackingEvent = {
          ...fullPendoConfig.visitor
        };

        /* This code is implemented to send analytics  */
        if (this.pendoParams?.initializeUserTrackingEvent) {
          const duration = 60000;
          this.initializeUserTrackingEvent(duration, userTrackingEvent);
        }
      });
    };
  }

  /* User tracking events are used to generate a Pendo like report on Google Analytics */
  initializeUserTrackingEvent(duration: number, eventData: any) {
    if (!duration) {
      duration = 60000;
    }

    if (!eventData) {
      eventData = {
        eventName: 'user_tracking'
      };
    }

    if (this.isIosWebView()) {
      eventData.hostDevice = 'ios';
      delete eventData.isAndroidWebView;
      delete eventData.isBrowserWebView;
    } else if (this.isAndroidWebView()) {
      eventData.hostDevice = 'android';
      delete eventData.isIosWebView;
      delete eventData.isBrowserWebView;
    } else {
      eventData.hostDevice = 'browser';
      delete eventData.isIosWebView;
      delete eventData.isAndroidWebView;
    }

    /* Ping analytics user tracking event */
    const analyticsEvent = window.setInterval(function () {
      (window as any)['dataLayer'] = (window as any)['dataLayer'] || [];
      (window as any)['dataLayer'].push(eventData);
    }, duration);
  }

  trackEvent(event: string, metadata?: any) {
    pendo.track(event, metadata);
  }

  flushNow() {
    return pendo.flushNow();
  }
}
